import {FC, PropsWithChildren, ReactElement, ReactNode, useState} from 'react'

import {Button} from '@/components/common/content/Button'
import {ButtonSize, ButtonVariant} from '@/components/common/content/ButtonTypes'
import {Text, TextFontWeight, TextVariant} from '@/components/common/content/Text'
import {CloseIcon} from '@/components/common/icons/CloseIcon'
import {Link, Trans} from '@/i18n'
import {IconSize} from '@/theme/icons'
import {theme} from '@/theme/theme'
import {MOBILE_SCREENSIZE} from '@/utils/styles/breakpoints'

import {CheckCircleOutlineIcon} from '../icons/CheckCircleOutlineIcon'
import {ErrorIcon} from '../icons/ErrorIcon'
import {InfoIcon} from '../icons/InfoIcon'
import {OpenBookIcon} from '../icons/OpenBookIcon'
import {WarningIcon} from '../icons/WarningIcon'

export enum AlertVariant {
  info = 'info',
  success = 'success',
  error = 'error',
  warning = 'warning',
  neutral = 'neutral',
}

export const alertVariants = {
  [AlertVariant.info]: {
    icon: InfoIcon,
    palette: theme.palette.info,
  },
  [AlertVariant.success]: {
    icon: CheckCircleOutlineIcon,
    palette: theme.palette.success,
  },
  [AlertVariant.error]: {
    icon: ErrorIcon,
    palette: theme.palette.error,
  },
  [AlertVariant.warning]: {
    icon: WarningIcon,
    palette: theme.palette.warning,
  },
  [AlertVariant.neutral]: {
    icon: OpenBookIcon,
    palette: {
      light: theme.palette.butter[100],
      contrastText: theme.palette.gray[900],
      outlineColor: theme.palette.butter[400],
    },
  },
}

type AlertButtonProps = {
  text: string
  icon?: ReactNode
  iconPosition?: 'left' | 'right'
  onClick?: () => void
  dataTest?: string
}

const AlertButton: FC<AlertButtonProps> = ({text, icon, iconPosition, onClick, dataTest}) => (
  <Button
    type="button"
    variant={ButtonVariant.tertiary}
    size={ButtonSize.medium}
    textVariant={TextVariant.subtitle13}
    icon={icon}
    iconPosition={iconPosition}
    data-test={dataTest}
    onClick={onClick}
  >
    {text}
  </Button>
)

type AlertProps = {
  variant?: AlertVariant
  title?: string
  description?: string | ReactElement<typeof Trans>
  button?: AlertButtonProps & {
    linkHref?: string
    target?: string
  }
  isCloseable?: boolean
  handleClose?: () => void
  marginBottom?: string
}

export const Alert: FC<PropsWithChildren<AlertProps>> = ({
  variant = AlertVariant.neutral,
  title,
  description,
  button,
  children,
  isCloseable = false,
  handleClose,
  marginBottom,
}) => {
  const [isClosed, setIsClosed] = useState(false)

  if (isClosed) return null

  const AlertIcon = alertVariants[variant].icon
  const palette = alertVariants[variant].palette
  const textColor = palette.contrastText
  const backgroundColor = palette.light
  const borderColor = palette.outlineColor

  return (
    <>
      <div className="alert">
        <div className="content">
          <AlertIcon color={textColor} size={IconSize.medium} />
          <div className="wrapper">
            {title && (
              <Text
                variant={TextVariant.text14}
                fontWeight={TextFontWeight.semibold}
                color={textColor}
              >
                {title}
              </Text>
            )}
            {description && (
              <Text variant={TextVariant.text14} color={textColor}>
                {description}
              </Text>
            )}
            {children}
          </div>
        </div>
        {button &&
          (button.linkHref ? (
            <div className="button-wrapper">
              <Link href={button.linkHref} onClick={button.onClick} target={button.target}>
                <AlertButton
                  text={button.text}
                  icon={button.icon}
                  iconPosition={button.iconPosition}
                  dataTest={button.dataTest}
                />
              </Link>
            </div>
          ) : (
            <div className="button-wrapper">
              <AlertButton
                text={button.text}
                icon={button.icon}
                iconPosition={button.iconPosition}
                onClick={button.onClick}
                dataTest={button.dataTest}
              />
            </div>
          ))}
        {isCloseable && (
          <button
            type="button"
            onClick={() => {
              setIsClosed(true)

              if (handleClose) {
                handleClose()
              }
            }}
          >
            <CloseIcon color={textColor} />
          </button>
        )}
      </div>

      <style jsx>{`
        .alert {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: ${theme.spacing[1]};
          padding: ${theme.spacing[2]};
          background-color: ${backgroundColor};
          border-radius: 12px;
          box-shadow: inset 0px 0px 0px 1px ${borderColor};
          position: relative;
          ${marginBottom ? `margin-bottom: ${marginBottom}` : ''};
        }
        .content {
          display: flex;
          padding: 10px ${theme.spacing[2]};
          text-align: left;
          color: ${textColor};
        }
        .wrapper {
          margin-left: ${theme.spacing[2.5]};
        }
        .button-wrapper {
          white-space: nowrap;
        }
        @media ${MOBILE_SCREENSIZE} {
          .alert {
            flex-wrap: ${isCloseable ? 'nowrap' : 'wrap'};
          }
          .button-wrapper {
            margin-left: auto;
          }
        }
      `}</style>
    </>
  )
}
